import { IProperty, ITreeProperty, ProductBase } from './product-base';
import { IProductPacking } from '../../product/models/product-card';
import { EMPTY_PRODUCT_IMAGE_PATH } from '../../core/services/products.service';
import { ProductGroupRotation } from './product-group-rotation';
import { Utils } from '../../core/services/utils.service';

const previewWidth: number = 140, previewHeight: number = 100;

export interface ProductParams {
	id: number,
	name: string,
	width: number,
	height: number,
	depth: number,
	is_loose: boolean,
	photo: string,
	brands: Array<IProperty>,
	article: string,
	planedPercent: number,
	packings: Array<any>,
	is_active: boolean,
	is_new: boolean,
	readonly assortment_type: IProperty,
	readonly statuses: Array<IProperty>,
	sync?: boolean,
	barcode?: string,
	categories: Array<Array<ITreeProperty>>,
	markers: Array<Array<ITreeProperty>>,
	suppliers: Array<IProperty>,
	price?: string,
	width_compression_coef?: number,
	height_compression_coef?: number,
	depth_compression_coef?: number,
	stock_qty: number,
	sales_qty: number,
	recommended_faces?: number,
	total_plan_faces?: number,
	avgSalesPerDay?: number,
	is_master_position: boolean
}

export class Product extends ProductBase {
	previewWidth: number;
	previewHeight: number;
	planedPercent: number = 0;
	realPercent: number = 0;
	is_loose: boolean = false;
	avgSalesPerDay: number;
	sync: boolean = true;
	packings: Array<IProductPacking>;
	active?: boolean;
	inactive?: boolean;
	is_active: boolean = false;
	readonly statuses: Array<IProperty>;
	readonly assortment_type: IProperty;
	is_new: boolean = false;
	abc_turnover?: any;
	abc_receipts?: any;
	stock_qty: number;
	sales_qty: number;
	readonly recommendedFacesQty?: number;
	readonly totalPlanFacesQty?: number;
	selected?: boolean; // TODO take out of class

	constructor(params: ProductParams) {
		super(Product.productToProductBase(params));
		if (params.categories?.length) this.categories = params.categories;
		if (params.markers?.length) this.markers = params.markers;
		if (params.is_loose) this.is_loose = params.is_loose;
		if (params.is_active) this.is_active = params.is_active;
		if (params.is_new) this.is_new = params.is_new;

		this.avgSalesPerDay = params.avgSalesPerDay;
		this.packings = params.packings || [];
		this.statuses = params.statuses ?? [];
		this.assortment_type = params.assortment_type ?? { id: undefined, name: undefined };
		this.planedPercent = params.planedPercent;
		this.recommendedFacesQty = params.recommended_faces;
		this.totalPlanFacesQty = params.total_plan_faces;
		this.stock_qty = params.stock_qty;
		this.sales_qty = params.sales_qty;
		this.is_master_position = params.is_master_position;

		this.setPreviewSizes();
	}

	get statusesUniqueId(): string {
		return Utils.unique<number>(this.statuses.map((status: IProperty) => status.id))
			.sort((a: number, b: number) => a - b)
			.join('-');
	}

	get statusesUniqueName(): string {
		return Utils.unique<string>(this.statuses.map((status: IProperty) => status.name)).sort().join('; ');
	}

	copy() {
		const {
				id, name, width, height, depth,
				is_loose, photo, article, planedPercent, packings,
				is_active, is_new, sync, barcode,
				categories, markers, brands, suppliers, price, width_compression_coef, height_compression_coef,
				depth_compression_coef, stock_qty, sales_qty, avgSalesPerDay, is_master_position
			} = this,
			params: ProductParams = {
				id,
				name,
				width,
				height,
				depth,
				is_loose,
				photo,
				article,
				planedPercent,
				packings,
				is_active,
				is_new,
				sync,
				barcode,
				categories,
				markers,
				brands,
				suppliers,
				price,
				width_compression_coef,
				height_compression_coef,
				depth_compression_coef,
				stock_qty,
				sales_qty,
				avgSalesPerDay,
				is_master_position,
				recommended_faces: this.recommendedFacesQty,
				total_plan_faces: this.totalPlanFacesQty,
				statuses: this.statuses,
				assortment_type: this.assortment_type
			};

		return new Product(params);
	}

	loadImage(): void {
		this.image = new Image();
		this.image.crossOrigin = 'anonymous';
		this.image.onerror = () => {
			this.image.onerror = null;
			this.image.src = EMPTY_PRODUCT_IMAGE_PATH;
			return true;
		};

		this.image.src = this.photo || EMPTY_PRODUCT_IMAGE_PATH;

		this.packings.forEach((packing: IProductPacking) => {
			packing.image = new Image();
			packing.image.crossOrigin = 'anonymous';
			packing.image.onerror = () => {
				packing.image.onerror = null;
				packing.image.src = EMPTY_PRODUCT_IMAGE_PATH;
				return true;
			};
			packing.image.src = packing.photo || EMPTY_PRODUCT_IMAGE_PATH;
		});
	}

	setPreviewSizes() {
		if (this.width > this.height) {
			this.previewWidth = previewWidth;
			this.previewHeight = Math.round(previewWidth * this.height / this.width);
		} else {
			this.previewHeight = previewHeight;
			this.previewWidth = Math.round(previewHeight * this.width / this.height);
		}
	}

	Depth(rotation: ProductGroupRotation) {
		switch (rotation) {
			case ProductGroupRotation.Side:return this.width;
			case ProductGroupRotation.Top:return this.height;
			default:return this.depth;
		}
	}

	static productToProductBase(params: ProductParams) {
		return {
			id: params.id,
			name: params.name,
			width: params.width,
			height: params.height,
			depth: params.depth,
			photo: params.photo,
			brands: params.brands,
			categories: params.categories,
			article: params.article,
			barcode: params.barcode,
			suppliers: params.suppliers,
			markers: params.markers,
			price: params.price,
			width_compression_coef: params.width_compression_coef,
			depth_compression_coef: params.depth_compression_coef,
			height_compression_coef: params.height_compression_coef,
			is_active: params.is_active,
			is_new: params.is_new,
			is_master_position: params.is_master_position,
		};
	}
}
